/**
 * ============================================================================
 * USER / TENANT ROUTES: Workspace Route Definitions
 * ============================================================================
 * Purpose:
 *   Defines routes and element mappings for Tenant Database workspace screens
 *   (Campaigns, Templates, Contacts, Phone Numbers, Ledgers, Messages, Support).
 * ============================================================================
 */

import React from "react";
import { Route } from "react-router-dom";

import CampaignManagement from "../user/pages/campaigns/CampaignManagement";
import CreateCampaign from "../user/pages/campaigns/CreateCampaign";
import GeneralMessages from "../user/pages/messages/GeneralMessages";
import TemplateManagement from "../user/pages/templates/TemplateManagement";
import CreateTemplate from "../user/pages/templates/CreateTemplate";
import ContactManagement from "../user/pages/contacts/ContactManagement";
import PhoneNumberList from "../user/pages/phoneNumbers/PhoneNumberList";
import LedgerManagement from "../user/pages/ledgers/LedgerManagement";
import MessageLogView from "../user/pages/messages/MessageLogView";
import SupportTickets from "../user/pages/tickets/SupportTickets";
import ReportManagement from "../user/pages/reports/ReportManagement";
import ApiDocumentation from "../user/pages/docs/ApiDocumentation";
import TinyUrls from "../user/pages/tinyUrls/TinyUrls";
import TinyUrlAnalytics from "../user/pages/tinyUrls/TinyUrlAnalytics";
import CreateTinyUrl from "../user/pages/tinyUrls/CreateTinyUrl";
import Chat from "../user/pages/chat/Chat";
import IndiaMartIntegration from "../user/pages/integrations/IndiaMartIntegration";
import WhatsappEmbeddedSignup from "../user/pages/whatsapp/WhatsappEmbeddedSignup";
import UserQueueManagement from "../master/pages/users/UserQueueManagement";
import DynamicImageTemplates from "../user/pages/dynamicImages/DynamicImageTemplates";
import DynamicImageEditor from "../user/pages/dynamicImages/DynamicImageEditor";
import ImageCampaign from "../user/pages/dynamicImages/ImageCampaign";
import PermissionRoute from "./PermissionRoute";

export const userRoutes = (
  <>
    {/* Scoped workspace routes */}
    <Route path="whatsapp-signup" element={<WhatsappEmbeddedSignup />} />
    <Route path="workspace/whatsapp-signup" element={<WhatsappEmbeddedSignup />} />
    <Route path="workspace/whatsapp-embedded" element={<WhatsappEmbeddedSignup />} />
    <Route path="workspace/users/queue/:id" element={<UserQueueManagement />} />
    <Route path="workspace/users/:id/queue" element={<UserQueueManagement />} />
    <Route path="workspace/queue-management" element={<UserQueueManagement />} />
    <Route path="workspace/queue-management/:id" element={<UserQueueManagement />} />
    
    {/* Integrations (Default: Disabled) */}
    <Route path="workspace/integrations/indiamart" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />
    <Route path="workspace/integrations" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />
    <Route path="workspace/indiamart" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />

    {/* Tiny URLs (Default: Disabled) */}
    <Route path="workspace/tiny-urls" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrls /></PermissionRoute>} />
    <Route path="workspace/tiny-urls/create" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><CreateTinyUrl /></PermissionRoute>} />
    <Route path="workspace/tiny-urls/edit/:id" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><CreateTinyUrl /></PermissionRoute>} />
    <Route path="workspace/tiny-urls/:id/analytics" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />
    <Route path="workspace/tiny-urls/analytics/:id" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />
    <Route path="workspace/links" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrls /></PermissionRoute>} />
    <Route path="workspace/links/:id/analytics" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />

    <Route path="workspace/docs" element={<ApiDocumentation />} />
    <Route path="workspace/api-docs" element={<ApiDocumentation />} />
    <Route path="workspace/campaigns" element={<CampaignManagement />} />
    <Route path="workspace/campaigns/create" element={<CreateCampaign />} />
    <Route path="workspace/campaigns/edit/:id" element={<CreateCampaign />} />

    {/* Dynamic Images & Image Camp workspace routes (Default: Disabled) */}
    <Route path="workspace/dynamic-images" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="workspace/dynamic-images/templates" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="workspace/dynamic-images/create" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageEditor /></PermissionRoute>} />
    <Route path="workspace/dynamic-images/edit/:id" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageEditor /></PermissionRoute>} />
    <Route path="workspace/image-camp" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign /></PermissionRoute>} />
    <Route path="workspace/image-camp/create" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign defaultTab="create" /></PermissionRoute>} />
    <Route path="workspace/image-camp/history" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign defaultTab="history" /></PermissionRoute>} />
    <Route path="workspace/image-camp/templates" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="workspace/image-campaign" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign /></PermissionRoute>} />

    <Route path="workspace/general-messages" element={<GeneralMessages />} />
    <Route path="workspace/reports" element={<ReportManagement />} />
    <Route path="workspace/reports/general" element={<ReportManagement defaultTab="general" />} />
    <Route path="workspace/reports/campaigns" element={<ReportManagement defaultTab="campaigns" />} />
    <Route path="workspace/reports/api" element={<ReportManagement defaultTab="api" />} />
    <Route path="workspace/templates" element={<TemplateManagement />} />
    <Route path="workspace/templates/create" element={<CreateTemplate />} />
    <Route path="workspace/templates/edit/:id" element={<CreateTemplate />} />
    <Route path="workspace/contacts" element={<ContactManagement />} />
    <Route path="workspace/groups" element={<ContactManagement defaultTab="groups" />} />
    <Route path="workspace/phone-numbers" element={<PhoneNumberList />} />
    <Route path="workspace/ledgers" element={<LedgerManagement />} />
    <Route path="workspace/messages" element={<MessageLogView />} />

    {/* Live Chat (Default: Enabled, Protected if disabled) */}
    <Route path="workspace/chat" element={<PermissionRoute permission="CHAT_MANAGEMENT"><Chat /></PermissionRoute>} />
    <Route path="workspace/tickets" element={<SupportTickets />} />

    {/* User client routes */}
    <Route path="user/phone-numbers" element={<PhoneNumberList />} />

    {/* Top-level aliases */}
    <Route path="campaigns" element={<CampaignManagement />} />
    <Route path="campaigns/create" element={<CreateCampaign />} />
    <Route path="campaigns/edit/:id" element={<CreateCampaign />} />
    <Route path="general-messages" element={<GeneralMessages />} />
    <Route path="reports" element={<ReportManagement />} />
    <Route path="reports/general" element={<ReportManagement defaultTab="general" />} />
    <Route path="reports/campaigns" element={<ReportManagement defaultTab="campaigns" />} />
    <Route path="reports/api" element={<ReportManagement defaultTab="api" />} />
    <Route path="templates" element={<TemplateManagement />} />
    <Route path="templates/create" element={<CreateTemplate />} />
    <Route path="templates/edit/:id" element={<CreateTemplate />} />
    <Route path="contacts" element={<ContactManagement />} />
    <Route path="groups" element={<ContactManagement defaultTab="groups" />} />
    <Route path="phone-numbers" element={<PhoneNumberList />} />
    <Route path="ledgers" element={<LedgerManagement />} />
    <Route path="messages" element={<MessageLogView />} />

    {/* Live Chat alias */}
    <Route path="chat" element={<PermissionRoute permission="CHAT_MANAGEMENT"><Chat /></PermissionRoute>} />
    <Route path="tickets" element={<SupportTickets />} />

    {/* Tiny URLs aliases */}
    <Route path="tiny-urls" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrls /></PermissionRoute>} />
    <Route path="tiny-urls/create" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><CreateTinyUrl /></PermissionRoute>} />
    <Route path="tiny-urls/edit/:id" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><CreateTinyUrl /></PermissionRoute>} />
    <Route path="tiny-urls/:id/analytics" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />
    <Route path="tiny-urls/analytics/:id" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />
    <Route path="links" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrls /></PermissionRoute>} />
    <Route path="links/:id/analytics" element={<PermissionRoute permission="TINY_URL_MANAGEMENT"><TinyUrlAnalytics /></PermissionRoute>} />

    <Route path="docs" element={<ApiDocumentation />} />
    <Route path="api-docs" element={<ApiDocumentation />} />
    <Route path="documentation" element={<ApiDocumentation />} />

    {/* Integrations aliases */}
    <Route path="integrations/indiamart" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />
    <Route path="integrations" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />
    <Route path="indiamart" element={<PermissionRoute permission="INDIAMART_INTEGRATION"><IndiaMartIntegration /></PermissionRoute>} />

    {/* Dynamic Images & Image Camp top-level aliases */}
    <Route path="dynamic-images" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="dynamic-images/templates" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="dynamic-images/create" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageEditor /></PermissionRoute>} />
    <Route path="dynamic-images/edit/:id" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageEditor /></PermissionRoute>} />
    <Route path="image-camp" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign /></PermissionRoute>} />
    <Route path="image-camp/create" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign defaultTab="create" /></PermissionRoute>} />
    <Route path="image-camp/history" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign defaultTab="history" /></PermissionRoute>} />
    <Route path="image-camp/templates" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><DynamicImageTemplates /></PermissionRoute>} />
    <Route path="image-campaign" element={<PermissionRoute permission="IMAGE_CAMP_MANAGEMENT"><ImageCampaign /></PermissionRoute>} />
  </>
);


export default userRoutes;
